import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DoorOpen, ImagePlus, Warehouse } from 'lucide-react';
import api from '../services/api';
import adminService from '../services/adminService';
import { useStore } from '../stores/useStore';
import GalleryModal from '../components/admin/GalleryModal';
import RpgCityCanvas from '../components/rpg/RpgCityCanvas';
import RpgMiniGameNaveModal from '../components/rpg/RpgMiniGameNaveModal';

const INTERACTION_ACTION_LABELS = {
  city_shop: 'Entrar en tienda',
  city_workshop: 'Entrar en taller',
  city_warehouse: 'Abrir almacen',
  city_hangar: 'Abrir hangar',
  city_transport: 'Ir a transporte',
  'mini-juego-nave': 'Jugar nave',
  'mini-juego-tragaperra': 'Jugar tragaperra',
  door: 'Entrar',
  entry: 'Salir de la Cúpula',
  portal: 'Abrir portal',
  plot: 'Parcela',
  defense: 'Abrir defensa',
  note: 'Leer'
};

const ACCESS_DIRECTION_LABELS = {
  any: 'Cualquier lado',
  center: 'Centro',
  north: 'Norte',
  south: 'Sur',
  east: 'Este',
  west: 'Oeste'
};

function isEditableTarget(target) {
  if (!target) return false;
  const tag = String(target.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
}

function getTileInteraction(tile) {
  const interaction = tile?.metadata?.interaction;
  if (!interaction || typeof interaction !== 'object' || interaction.enabled === false) return null;
  return {
    type: String(interaction.type || 'note').trim() || 'note',
    label: String(interaction.label || '').trim(),
    description: String(interaction.description || '').trim(),
    target: String(interaction.target || '').trim(),
    access_from: String(interaction.access_from || 'any').trim() || 'any'
  };
}

function getRelativeDirection(dx, dy) {
  if (dx === 0 && dy === 0) return 'center';
  if (dx === 0 && dy === -1) return 'north';
  if (dx === 0 && dy === 1) return 'south';
  if (dx === 1 && dy === 0) return 'east';
  if (dx === -1 && dy === 0) return 'west';
  return null;
}

function matchesAccessFrom(accessFrom, relation) {
  const normalizedAccess = String(accessFrom || 'any').trim() || 'any';
  if (normalizedAccess === 'any') return true;
  if (normalizedAccess === 'center') return relation === 'center';
  return normalizedAccess === relation;
}

function resolveInteriorRoute(target, type) {
  const normalizedTarget = String(target || '').trim();
  if (normalizedTarget.startsWith('/rpg/interior/')) return normalizedTarget;
  if (normalizedTarget.startsWith('interior:')) {
    return `/rpg/interior/${normalizedTarget.slice('interior:'.length).trim() || 'default'}`;
  }
  if (['door', 'portal'].includes(String(type || '').trim())) {
    if (normalizedTarget) {
      return `/rpg/interior/${normalizedTarget.replace(/^\/+/, '').replace(/^interior:/, '')}`;
    }
    return '/rpg/interior/default';
  }
  return null;
}

function resolveImageSrc(image) {
  if (!image) return '';
  if (/^https?:\/\//i.test(image) || image.startsWith('blob:') || image.startsWith('/')) return image;
  const apiBase = import.meta.env.VITE_API_URL || '';
  return `${apiBase}/uploads/gallery/${image}`;
}

function formatSecondsLabel(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes <= 0) return `${remainder}s`;
  return `${minutes}m ${remainder.toString().padStart(2, '0')}s`;
}

export default function RpgCity() {
  const navigate = useNavigate();
  const { setRobot, user } = useStore();
  const isAdmin = Boolean(user?.isAdmin);
  const [loading, setLoading] = useState(true);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [error, setError] = useState('');
  const [context, setContext] = useState(null);
  const [scene, setScene] = useState(null);
  const [player, setPlayer] = useState(null);
  const [movementStatus, setMovementStatus] = useState('ready');
  const [syncStatus, setSyncStatus] = useState('idle');
  const [interactionMessage, setInteractionMessage] = useState('');
  const [interactionDialog, setInteractionDialog] = useState(null);
  const [miniGameNaveDialog, setMiniGameNaveDialog] = useState(null);
  const [plotNotice, setPlotNotice] = useState('');
  const [playerVisual, setPlayerVisual] = useState(null);
  const [farm, setFarm] = useState(null);
  const [panelImagePickerOpen, setPanelImagePickerOpen] = useState(false);
  const lastSavedRef = useRef({ x: null, y: null });
  const playerRef = useRef(null);
  const saveTimerRef = useRef(null);
  const moveAnimationRef = useRef(null);
  const movingRef = useRef(false);
  const heldDirectionRef = useRef(null);
  const handleCanvasReady = useCallback(() => {
    if (!movingRef.current) {
      setMovementStatus('ready');
    }
  }, []);

  const panelImage = useMemo(() => resolveImageSrc(context?.rpg?.config?.background_image || ''), [context?.rpg?.config?.background_image]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setClockNow(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  const handleSelectPanelImage = useCallback(async (filename) => {
    if (!isAdmin || !context?.city?.id) return;
    const currentConfig = context?.rpg?.config || {};
    const tilesets = Array.isArray(currentConfig.tilesets) ? currentConfig.tilesets : [];
    try {
      await adminService.saveRpgCityConfig(context.city.id, {
        dome_theme: currentConfig.dome_theme || 'neutral',
        background_image: filename,
        is_enabled: currentConfig.is_enabled !== false,
        metadata: currentConfig.metadata || {},
        tilesets
      });
      setContext((current) => {
        if (!current) return current;
        return {
          ...current,
          rpg: current.rpg ? {
            ...current.rpg,
            config: {
              ...(current.rpg.config || {}),
              background_image: filename
            }
          } : current.rpg
        };
      });
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar la imagen de la Cúpula');
    } finally {
      setPanelImagePickerOpen(false);
    }
  }, [context?.city?.id, context?.rpg?.config, isAdmin]);

  const closeInteractionDialog = useCallback(() => {
    setInteractionDialog(null);
    setPlotNotice('');
  }, []);

  const confirmEntryExit = useCallback(() => {
    setInteractionDialog(null);
    setPlotNotice('');
    navigate('/city');
  }, [navigate]);

  const openCityWarehouse = useCallback(() => {
    setInteractionDialog(null);
    setPlotNotice('');
    navigate('/city/warehouse', { state: { fromRpg: true } });
  }, [navigate]);

  const stopMoveAnimation = useCallback(() => {
    if (moveAnimationRef.current) {
      window.cancelAnimationFrame(moveAnimationRef.current);
      moveAnimationRef.current = null;
    }
    movingRef.current = false;
    setPlayerVisual(null);
  }, []);

  useEffect(() => {
    playerRef.current = player;
  }, [player]);

  const loadRpgContext = useCallback(async (options = {}) => {
    const silent = Boolean(options?.silent);
    if (!silent) {
      setLoading(true);
    }
    setError('');
    try {
      const [contextRes, appearanceRes] = await Promise.all([
        api.get(`/robots/my-city-rpg-context?t=${Date.now()}`),
        api.get(`/robots/rpg-appearance?t=${Date.now()}`)
      ]);
      const nextContext = contextRes.data;
      const latestAppearance = appearanceRes.data?.appearance || null;
      const nextFarm = nextContext.rpg?.farm || { groupScope: null, plantables: [] };
      const currentScene = String(nextContext.rpg?.player?.metadata?.current_scene || '').trim();
      if (currentScene.startsWith('interior:')) {
        const interiorKey = currentScene.slice('interior:'.length).trim() || 'default';
        navigate(`/rpg/interior/${interiorKey}`, { replace: true });
        return;
      }
      const nextPlayer = nextContext.rpg?.player
        ? {
            ...nextContext.rpg.player,
            appearance: latestAppearance || nextContext.rpg.player.appearance || null
          }
        : null;
      setContext(nextContext);
      setScene(nextContext.rpg?.scene || null);
      setPlayer(nextPlayer);
      setFarm(nextFarm);
      if (nextContext.bot) setRobot(nextContext.bot);
      lastSavedRef.current = {
        x: Number(nextPlayer?.x ?? 0),
        y: Number(nextPlayer?.y ?? 0)
      };
      setSyncStatus('idle');
      setMovementStatus('ready');
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar la ciudad RPG');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [navigate, setRobot]);

  useEffect(() => {
    void loadRpgContext();
  }, [loadRpgContext]);

  useEffect(() => () => {
    stopMoveAnimation();
    clearTimeout(saveTimerRef.current);
  }, [stopMoveAnimation]);

  const blockedSet = useMemo(() => {
    const set = new Set();
    (scene?.tiles || []).forEach((tile) => {
      if (Number(tile.blocked) === 1) {
        set.add(Number(tile.coordinate));
      }
    });
    return set;
  }, [scene?.tiles]);

  const interactiveTileMap = useMemo(() => {
    const map = new Map();
    (scene?.tiles || []).forEach((tile) => {
      const interaction = getTileInteraction(tile);
      if (!interaction) return;
      const coordinate = Number(tile.coordinate);
      const current = map.get(coordinate);
      if (!current || Number(tile.layer) >= Number(current.layer || 0)) {
        map.set(coordinate, {
          coordinate,
          layer: Number(tile.layer) || 0,
          interaction,
          tile
        });
      }
    });
    return map;
  }, [scene?.tiles]);

  const playerFacing = useMemo(() => {
    if (!player) return 'south';
    return String(player.facing || player.direction || 'south').trim() || 'south';
  }, [player]);

  const canMoveTo = useCallback((x, y) => {
    const width = Number(scene?.width) || 0;
    const height = Number(scene?.height) || 0;
    if (!Number.isInteger(x) || !Number.isInteger(y)) return false;
    if (x < 0 || y < 0 || x >= width || y >= height) return false;
    return !blockedSet.has(y * width + x);
  }, [blockedSet, scene?.height, scene?.width]);

  const movePlayer = useCallback((dx, dy) => {
    const currentPlayer = playerRef.current;
    if (!scene || !currentPlayer || context?.transport || movingRef.current) return;
    const nextX = Number(currentPlayer.x || 0) + dx;
    const nextY = Number(currentPlayer.y || 0) + dy;
    if (!canMoveTo(nextX, nextY)) {
      setMovementStatus('blocked');
      return;
    }
    const nextFacing = dx > 0 ? 'east' : dx < 0 ? 'west' : dy > 0 ? 'south' : dy < 0 ? 'north' : playerFacing;
    const startX = Number(currentPlayer.x || 0);
    const startY = Number(currentPlayer.y || 0);
    const duration = 180;
    const startTime = window.performance.now();
    movingRef.current = true;
    setMovementStatus('moving');
    setPlayerVisual({ x: startX, y: startY });

    const animate = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - ((-2 * progress + 2) ** 2) / 2;
      setPlayerVisual({
        x: startX + ((nextX - startX) * eased),
        y: startY + ((nextY - startY) * eased)
      });
      if (progress < 1) {
        moveAnimationRef.current = window.requestAnimationFrame(animate);
        return;
      }
      stopMoveAnimation();
      const nextPlayer = { ...currentPlayer, x: nextX, y: nextY, facing: nextFacing };
      playerRef.current = nextPlayer;
      setPlayer(nextPlayer);
      if (!heldDirectionRef.current) {
        setMovementStatus('ready');
      }
    };

    moveAnimationRef.current = window.requestAnimationFrame(animate);
  }, [canMoveTo, context?.transport, playerFacing, scene, stopMoveAnimation]);

  const nearbyInteraction = useMemo(() => {
    if (!scene || !player) return null;
    const width = Number(scene.width) || 0;
    const px = Number(player.x);
    const py = Number(player.y);
    const candidates = [
      { x: px, y: py, relation: 'center' },
      { x: px, y: py - 1, relation: 'north' },
      { x: px + 1, y: py, relation: 'east' },
      { x: px, y: py + 1, relation: 'south' },
      { x: px - 1, y: py, relation: 'west' }
    ];

    for (const candidate of candidates) {
      const { x, y, relation } = candidate;
      if (!Number.isInteger(x) || !Number.isInteger(y)) continue;
      if (x < 0 || y < 0 || x >= Number(scene.width) || y >= Number(scene.height)) continue;
      const coordinate = (y * width) + x;
      const found = interactiveTileMap.get(coordinate);
      if (found && matchesAccessFrom(found.interaction?.access_from, relation)) {
        return { ...found, relation };
      }
    }

    return null;
  }, [interactiveTileMap, player, scene]);

  const cropTileMap = useMemo(() => {
    const map = new Map();
    (scene?.crops || []).forEach((crop) => {
      const coordinate = Number(crop.coordinate || 0);
      const layer = Number(crop.layer || 0);
      map.set(`${layer}:${coordinate}`, crop);
    });
    return map;
  }, [scene?.crops]);

  const nearbyPlotCrop = useMemo(() => {
    if (!nearbyInteraction || nearbyInteraction.interaction?.type !== 'plot') return null;
    return cropTileMap.get(`${Number(nearbyInteraction.layer || 0)}:${Number(nearbyInteraction.coordinate || 0)}`) || null;
  }, [cropTileMap, nearbyInteraction]);

  const farmPlantables = useMemo(() => {
    const plantables = Array.isArray(farm?.plantables) ? farm.plantables : [];
    return [...plantables].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es'));
  }, [farm?.plantables]);

  const liveCrop = useMemo(() => {
    const crop = interactionDialog?.crop;
    if (!crop) return null;
    const stageCount = Math.max(1, Number(crop.stage_count || 7));
    const growthSeconds = Math.max(1, Number(crop.growth_seconds || 420));
    const plantedAt = crop.planted_at ? new Date(crop.planted_at).getTime() : 0;
    const readyAt = crop.ready_at ? new Date(crop.ready_at).getTime() : 0;
    const totalMs = growthSeconds * 1000;
    const remainingSeconds = readyAt > 0
      ? Math.max(0, Math.ceil((readyAt - clockNow) / 1000))
      : Math.max(0, Number(crop.remaining_seconds || 0));
    const progress = plantedAt > 0
      ? Math.min(1, Math.max(0, (clockNow - plantedAt) / totalMs))
      : Math.max(0, Number(crop.progress || 0));
    const stage = plantedAt > 0
      ? Math.min(stageCount, Math.max(1, Math.floor(progress * stageCount) + 1))
      : Math.max(1, Number(crop.stage || 1));
    return {
      ...crop,
      stage,
      stage_count: stageCount,
      progress,
      remaining_seconds: remainingSeconds,
      is_ready: remainingSeconds <= 0
    };
  }, [clockNow, interactionDialog?.crop]);

  const sceneWithLiveCrops = useMemo(() => {
    if (!scene) return scene;
    const crops = Array.isArray(scene.crops) ? scene.crops.map((crop) => {
      if (!crop) return crop;
      const growthSeconds = Math.max(1, Number(crop.growth_seconds || 420));
      const stageCount = Math.max(2, Number(crop.stage_count || 7));
      const plantedAt = crop.planted_at ? new Date(crop.planted_at).getTime() : 0;
      const readyAt = crop.ready_at ? new Date(crop.ready_at).getTime() : 0;
      const totalMs = growthSeconds * 1000;
      const remainingSeconds = readyAt > 0
        ? Math.max(0, Math.ceil((readyAt - clockNow) / 1000))
        : Math.max(0, Number(crop.remaining_seconds || 0));
      const progress = plantedAt > 0
        ? Math.min(1, Math.max(0, (clockNow - plantedAt) / totalMs))
        : Math.max(0, Number(crop.progress || 0));
      const stage = plantedAt > 0
        ? Math.min(stageCount, Math.max(1, Math.floor(progress * stageCount) + 1))
        : Math.max(1, Number(crop.stage || 1));
      return {
        ...crop,
        stage,
        stage_count: stageCount,
        progress,
        remaining_seconds: remainingSeconds,
        is_ready: remainingSeconds <= 0
      };
    }) : scene.crops;
    return { ...scene, crops };
  }, [clockNow, scene]);

  const handleInteraction = useCallback(() => {
    if (!nearbyInteraction || movingRef.current) return;
    const interaction = nearbyInteraction.interaction || {};
    const target = String(interaction.target || '').trim();
    const type = String(interaction.type || 'note').trim() || 'note';
    setInteractionMessage('');
    setPlotNotice('');

    if (type === 'note' || type === 'text') {
      setInteractionDialog({
        kind: 'note',
        title: interaction.label || 'Mensaje',
        message: interaction.description || 'No hay texto configurado para esta interaccion.'
      });
      return;
    }

    if (type === 'entry') {
      setInteractionDialog({
        kind: 'entry',
        title: interaction.label || 'Salida de la Cúpula',
        message: interaction.description || '¿Quieres salir de la Cúpula y volver a la ciudad?'
      });
      return;
    }

    if (type === 'plot') {
      setInteractionDialog({
        kind: 'plot',
        title: interaction.label || 'Parcela',
        message: nearbyPlotCrop
          ? (nearbyPlotCrop.is_ready
            ? 'La planta está lista para cosechar.'
            : `Cultivo en crecimiento. Quedan ${formatSecondsLabel(nearbyPlotCrop.remaining_seconds)}.`)
          : (interaction.description || 'Escoge una semilla o cultivo del almacén de la ciudad.'),
        crop: nearbyPlotCrop,
        plot: {
          coordinate: Number(nearbyInteraction.coordinate || 0),
          layer: Number(nearbyInteraction.layer || 0)
        }
      });
      return;
    }

    if (type === 'mini-juego-nave') {
      setMiniGameNaveDialog({
        type,
        label: interaction.label || 'Asalto aéreo',
        description: interaction.description || 'Despega y sobrevive a las oleadas enemigas.',
        game_assets: interaction.game_assets || null
      });
      return;
    }

    if (target.startsWith('/')) {
      navigate(target);
      return;
    }

    const interiorRoute = resolveInteriorRoute(target, type);
    if (interiorRoute) {
      navigate(interiorRoute);
      return;
    }

    if (type === 'city_shop') {
      navigate('/city/shop?rpg=1');
      return;
    }
    if (type === 'city_workshop') {
      navigate('/city/workshop');
      return;
    }
    if (type === 'city_warehouse') {
      navigate('/city/warehouse', { state: { fromRpg: true } });
      return;
    }
    if (type === 'city_hangar') {
      navigate('/city/hangar');
      return;
    }
    if (type === 'city_transport') {
      navigate('/city/transport');
      return;
    }

    setInteractionMessage(interaction.description || interaction.label || 'Este punto interactivo todavia no tiene una accion conectada.');
  }, [nearbyInteraction, nearbyPlotCrop, navigate]);

  const handlePlantCrop = useCallback(async (plantable) => {
    if (!nearbyInteraction || !plantable) return;
    try {
      setPlotNotice('');
      setSyncStatus('saving');
      await api.post('/robots/my-city-rpg-crops/plant', {
        coordinate: Number(nearbyInteraction.coordinate || 0),
        layer: Number(nearbyInteraction.layer || 0),
        item_id: Number(plantable.id || 0),
        source_storage_type: String(plantable.storage_source_type || '')
      });
      setInteractionDialog(null);
      setPlotNotice('');
      await loadRpgContext({ silent: true });
      setSyncStatus('idle');
    } catch (err) {
      setSyncStatus('error');
      setError(err.response?.data?.error || 'No se pudo plantar el cultivo');
    }
  }, [loadRpgContext, nearbyInteraction]);

  const handleHarvestCrop = useCallback(async () => {
    if (!nearbyInteraction) return;
    try {
      setPlotNotice('');
      setError('');
      setSyncStatus('saving');
      const response = await api.post('/robots/my-city-rpg-crops/harvest', {
        coordinate: Number(nearbyInteraction.coordinate || 0),
        layer: Number(nearbyInteraction.layer || 0)
      });
      await loadRpgContext({ silent: true });
      const storedLocation = String(response?.data?.stored_location || '').trim();
      const harvestedName = String(response?.data?.harvested_item_name || '').trim();
      setPlotNotice(
        storedLocation === 'personal_fallback'
          ? `La cosecha${harvestedName ? ` de ${harvestedName}` : ''} se guardó en tu almacén personal.`
          : `La cosecha${harvestedName ? ` de ${harvestedName}` : ''} se guardó en el almacén de origen.`
      );
      setInteractionDialog((current) => (current && current.kind === 'plot'
        ? {
            ...current,
            crop: null,
            message: current.message || 'La cosecha se guardó correctamente.'
          }
        : current));
      setSyncStatus('idle');
    } catch (err) {
      setSyncStatus('error');
      setError(err.response?.data?.error || 'No se pudo cosechar el cultivo');
    }
  }, [loadRpgContext, nearbyInteraction]);

  useEffect(() => {
    if (!heldDirectionRef.current || !player || !scene || context?.transport || movingRef.current) return;
    const { dx, dy } = heldDirectionRef.current;
    movePlayer(dx, dy);
  }, [context?.transport, movePlayer, player, scene]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isEditableTarget(event.target)) return;
      const key = String(event.code || event.key || '');
      if (miniGameNaveDialog) {
        if (key === 'Escape') {
          event.preventDefault();
          setMiniGameNaveDialog(null);
        }
        return;
      }
      if (interactionDialog) {
        if (interactionDialog.kind === 'entry') {
          if (key === 'Escape') {
            event.preventDefault();
            closeInteractionDialog();
          } else if (key === 'Enter' || key === 'Space' || key === 'KeyE') {
            event.preventDefault();
            confirmEntryExit();
          }
        } else if (interactionDialog.kind === 'plot') {
          if (key === 'Escape' || key === 'Enter' || key === 'Space' || key === 'KeyE') {
            event.preventDefault();
            closeInteractionDialog();
          }
        } else if (key === 'Escape' || key === 'Enter' || key === 'Space' || key === 'KeyE') {
          event.preventDefault();
          closeInteractionDialog();
        }
        return;
      }
      if (key === 'ArrowUp' || key === 'KeyW') {
        event.preventDefault();
        heldDirectionRef.current = { dx: 0, dy: -1 };
        movePlayer(0, -1);
      } else if (key === 'ArrowDown' || key === 'KeyS') {
        event.preventDefault();
        heldDirectionRef.current = { dx: 0, dy: 1 };
        movePlayer(0, 1);
      } else if (key === 'ArrowLeft' || key === 'KeyA') {
        event.preventDefault();
        heldDirectionRef.current = { dx: -1, dy: 0 };
        movePlayer(-1, 0);
      } else if (key === 'ArrowRight' || key === 'KeyD') {
        event.preventDefault();
        heldDirectionRef.current = { dx: 1, dy: 0 };
        movePlayer(1, 0);
      } else if (key === 'KeyE') {
        if (!nearbyInteraction) return;
        event.preventDefault();
        handleInteraction();
      }
    };

    const onKeyUp = (event) => {
      const key = String(event.code || event.key || '');
      if (
        key === 'ArrowUp' || key === 'KeyW'
        || key === 'ArrowDown' || key === 'KeyS'
        || key === 'ArrowLeft' || key === 'KeyA'
        || key === 'ArrowRight' || key === 'KeyD'
      ) {
        heldDirectionRef.current = null;
        if (!movingRef.current) {
          setMovementStatus('ready');
        }
      }
    };

    window.addEventListener('keydown', onKeyDown, { passive: false });
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [closeInteractionDialog, confirmEntryExit, handleInteraction, interactionDialog, miniGameNaveDialog, movePlayer, nearbyInteraction]);

  useEffect(() => {
    if (!scene || !player || context?.transport) return undefined;
    const next = { x: Number(player.x), y: Number(player.y) };
    if (next.x === lastSavedRef.current.x && next.y === lastSavedRef.current.y) {
      return undefined;
    }

    clearTimeout(saveTimerRef.current);
    setSyncStatus('pending');
    saveTimerRef.current = window.setTimeout(async () => {
      try {
        setSyncStatus('saving');
        await api.post('/robots/my-city-rpg-context/position', next);
        lastSavedRef.current = next;
        setSyncStatus('idle');
      } catch (err) {
        setSyncStatus('error');
        setError(err.response?.data?.error || 'No se pudo guardar la posicion RPG');
      }
    }, 360);

    return () => clearTimeout(saveTimerRef.current);
  }, [context?.transport, player, scene]);

  const viewportPlayer = playerVisual || player;
  const viewport = useMemo(() => ({
    width: 13,
    height: 9,
    centerX: Number(viewportPlayer?.x || 0),
    centerY: Number(viewportPlayer?.y || 0)
  }), [scene?.width, viewportPlayer?.x, viewportPlayer?.y]);

  const canvasPlayer = useMemo(() => ({
    ...player,
    x: Number(viewportPlayer?.x ?? player?.x ?? 0),
    y: Number(viewportPlayer?.y ?? player?.y ?? 0),
    movement_state: movementStatus
  }), [movementStatus, player, viewportPlayer]);

  if (loading) {
    return <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">Cargando ciudad RPG...</div>;
  }

  if (error && !context) {
    return (
      <div className="min-h-screen bg-[#04060d] text-rose-200 flex items-center justify-center px-6 text-center">
        <div className="space-y-4 max-w-lg">
          <p className="text-xl font-bold">{error}</p>
          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate('/city')}
              className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-3 font-bold text-slate-950"
            >
              <ArrowLeft size={16} /> Volver a ciudad
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!context?.city || !scene || !player) {
    return (
      <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center px-6 text-center">
        <div className="space-y-4 max-w-lg">
          <p className="text-xl font-bold">{error || 'Esta ciudad no tiene un mundo RPG listo todavía.'}</p>
          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate('/city')}
              className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-3 font-bold text-slate-950"
            >
              <ArrowLeft size={16} /> Volver a ciudad
            </button>
          )}
        </div>
      </div>
    );
  }

  const canPlay = !context.transport;
  const tileSize = 64;

  return (
    <div className="min-h-screen bg-[#03050c] text-white">
      <div className="mx-auto max-w-7xl px-4 py-5 md:px-6 space-y-4">
        <header
          className="relative overflow-hidden flex flex-col gap-4 rounded-3xl border border-cyan-400/10 bg-[#07101f] p-4 md:p-5 shadow-[0_0_60px_rgba(12,18,36,0.45)]"
          style={panelImage ? { backgroundImage: `linear-gradient(135deg, rgba(7,16,31,0.94), rgba(7,16,31,0.88)), url(${panelImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#07101f]/0 via-[#07101f]/0 to-[#07101f]/0 pointer-events-none" />
          <div className="relative z-10 flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => navigate('/city')}
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.35em] text-cyan-300 hover:text-cyan-200"
                >
                  <ArrowLeft size={14} /> Volver a ciudad
                </button>
              )}
              <div className="space-y-1">
                <h1 className="text-2xl md:text-4xl font-black tracking-tight">{context.city.name}</h1>
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPanelImagePickerOpen(true)}
                  className="inline-flex items-center gap-2 rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-sm font-bold text-cyan-100 hover:bg-cyan-500/20"
                >
                  <ImagePlus size={16} />
                  Imagen
                </button>
              </div>
            )}
          </div>
        </header>

        {!canPlay && (
          <div className="rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4 text-sm text-amber-100">
            Tu BOT está en tránsito. No se puede abrir el runtime RPG mientras el viaje esté activo.
          </div>
        )}

        {interactionMessage && (
          <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 p-4 text-sm text-cyan-100">
            {interactionMessage}
          </div>
        )}

        {interactionDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6 backdrop-blur-sm">
            <button
              type="button"
              aria-label="Cerrar modal"
              className="absolute inset-0 h-full w-full cursor-default"
              onClick={closeInteractionDialog}
            />
            <div className="relative z-10 w-full max-w-lg rounded-3xl border border-cyan-400/20 bg-[#07101f] p-6 shadow-2xl shadow-cyan-950/40">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.35em] text-cyan-300">Interaccion</p>
                  <h3 className="mt-2 text-2xl font-black text-white">{interactionDialog.title}</h3>
                </div>
                <button
                  type="button"
                  onClick={closeInteractionDialog}
                  className="rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm font-bold text-slate-200 hover:border-cyan-400/40 hover:text-white"
                >
                  Cerrar
                </button>
              </div>
                {syncStatus === 'saving' && (
                  <div className="mt-4 rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">
                    Procesando la acción...
                  </div>
                )}
                {plotNotice && (
                  <div className="mt-4 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
                    {plotNotice}
                  </div>
                )}
                {error && (
                  <div className="mt-4 rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                    {error}
                  </div>
                )}
              <div className="mt-5 rounded-2xl border border-slate-800 bg-black/20 p-4 text-sm leading-6 text-slate-200">
                {interactionDialog.message}
              </div>
              {interactionDialog.kind === 'entry' ? (
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={closeInteractionDialog}
                    className="rounded-2xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-sm font-bold text-slate-200 hover:border-slate-500 hover:text-white"
                  >
                    Quedarte
                  </button>
                  <button
                    type="button"
                    onClick={confirmEntryExit}
                    className="rounded-2xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm font-bold text-amber-100 hover:bg-amber-500/20"
                  >
                    Salir de la Cúpula
                  </button>
                </div>
              ) : interactionDialog.kind === 'plot' ? (
                <div className="mt-5 space-y-4">
                  {liveCrop ? (
                    <div className="space-y-3 rounded-2xl border border-cyan-400/20 bg-cyan-500/5 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.25em] text-cyan-300">Cultivo actual</p>
                          <p className="mt-1 text-lg font-black text-white">
                            Fase {liveCrop.stage}/{liveCrop.stage_count}
                          </p>
                          <p className="mt-1 text-xs font-semibold text-cyan-100/80">
                            {liveCrop.crop_name || liveCrop.seed_name || 'Cultivo'}
                          </p>
                        </div>
                        <span className="rounded-full border border-cyan-400/20 px-3 py-1 text-xs font-bold text-cyan-200">
                          {Math.round((liveCrop.progress || 0) * 100)}%
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-900">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 transition-[width]"
                          style={{ width: `${Math.round((liveCrop.progress || 0) * 100)}%` }}
                        />
                      </div>
                      <div className="text-sm text-slate-300">
                        {liveCrop.is_ready
                          ? 'Ya puedes recoger la cosecha.'
                          : `Tiempo restante: ${formatSecondsLabel(liveCrop.remaining_seconds)}.`}
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={closeInteractionDialog}
                          className="rounded-2xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-sm font-bold text-slate-200 hover:border-slate-500 hover:text-white"
                        >
                          Cerrar
                        </button>
                          <button
                            type="button"
                            onClick={openCityWarehouse}
                            className="inline-flex items-center gap-2 rounded-2xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 text-sm font-bold text-cyan-100 hover:bg-cyan-500/20"
                          >
                            <Warehouse size={15} />
                            Abrir almacén
                          </button>
                          {liveCrop.is_ready && (
                            <button
                              type="button"
                              onClick={() => void handleHarvestCrop()}
                              disabled={syncStatus === 'saving'}
                              className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-sm font-bold text-emerald-100 hover:bg-emerald-500/20"
                            >
                              {syncStatus === 'saving' ? 'Cosechando...' : 'Cosechar'}
                            </button>
                          )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {farmPlantables.length > 0 ? (
                        <div className="grid grid-cols-1 gap-3">
                          {farmPlantables.map((plantable) => (
                            <button
                              key={`${plantable.storage_source_type}-${plantable.id}-${plantable.storage_row_id}`}
                              type="button"
                              onClick={() => void handlePlantCrop(plantable)}
                              className="flex items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-black/20 p-4 text-left hover:border-cyan-400/30 hover:bg-cyan-500/10"
                            >
                              <div className="flex items-center gap-3">
                                <div className="h-12 w-12 overflow-hidden rounded-xl border border-slate-700 bg-[#09111e]">
                                  {plantable.image ? (
                                    <img
                                      src={resolveImageSrc(plantable.image)}
                                      alt={plantable.name}
                                      className="h-full w-full object-cover"
                                    />
                                  ) : null}
                                </div>
                                <div>
                                  <p className="font-bold text-white">{plantable.name}</p>
                                  <p className="text-xs text-slate-400">
                                    {plantable.storage_source_type === 'personal' ? 'Almacén personal' : 'Almacén de ciudad'} · Stock: {plantable.storage_quantity}
                                    {' · '}
                                    Crecimiento: {formatSecondsLabel(plantable.growth_seconds)}
                                  </p>
                                </div>
                              </div>
                              <span className="rounded-full border border-cyan-400/20 px-3 py-1 text-xs font-bold text-cyan-200">
                                Plantar
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-slate-800 bg-black/20 p-4 text-sm leading-6 text-slate-200">
                          No hay semillas o cultivos plantables disponibles en el almacén de la ciudad.
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={closeInteractionDialog}
                          className="rounded-2xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-sm font-bold text-slate-200 hover:border-slate-500 hover:text-white"
                        >
                          Cerrar
                        </button>
                        <button
                          type="button"
                          onClick={openCityWarehouse}
                          className="inline-flex items-center gap-2 rounded-2xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 text-sm font-bold text-cyan-100 hover:bg-cyan-500/20"
                        >
                          <Warehouse size={15} />
                          Abrir almacén
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-4 text-xs uppercase tracking-[0.25em] text-slate-500">
                  Pulsa `E` o `Esc` para cerrar.
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
          <section className="rounded-3xl border border-cyan-400/10 bg-[#07101f] p-4 overflow-hidden">
              <div className="rounded-2xl border border-slate-800 bg-[#050914] overflow-hidden max-h-[calc(100vh-240px)]">
                <div className="flex min-w-max justify-center">
                <RpgCityCanvas
                  scene={sceneWithLiveCrops}
                  player={canvasPlayer}
                  tileSize={tileSize}
                  viewport={viewport}
                  onReady={handleCanvasReady}
                />
                </div>
              </div>
          </section>

          <aside className="space-y-4">
            <section className="rounded-3xl border border-slate-800 bg-[#07101f] p-4 space-y-3">
              <h2 className="flex items-center gap-2 text-lg font-bold text-cyan-300"><DoorOpen size={18} /> Interaccion</h2>
              {nearbyInteraction ? (
                <div className="space-y-3 text-sm text-slate-300">
                  <p className="font-bold text-white">{nearbyInteraction.interaction.label || 'Punto interactivo'}</p>
                  <p>{nearbyInteraction.interaction.description || 'Hay una accion disponible en esta zona de la Cupula.'}</p>
<div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleInteraction}
                      className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 font-bold text-cyan-100 hover:bg-cyan-500/20"
                    >
                      {INTERACTION_ACTION_LABELS[nearbyInteraction.interaction.type] || 'Interactuar'} (E)
                    </button>
</div>
                </div>
              ) : (
                <div className="text-sm text-slate-400">
                  Acercate a una tienda, taller o punto marcado en el editor para interactuar.
                </div>
              )}
            </section>

            
          </aside>
        </div>
      </div>
      {isAdmin && (
        <GalleryModal
          isOpen={panelImagePickerOpen}
          onClose={() => setPanelImagePickerOpen(false)}
          onSelect={(filename) => void handleSelectPanelImage(filename)}
        />
      )}
      <RpgMiniGameNaveModal
        isOpen={Boolean(miniGameNaveDialog)}
        onClose={() => setMiniGameNaveDialog(null)}
        cityId={context?.city?.id || null}
        cityName={context?.city?.name || context?.city?.title || 'Cúpula'}
        userName={user?.username || 'Jugador'}
        interaction={miniGameNaveDialog}
      />
    </div>
  );
}
